import React, {forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState} from 'react';
import {useTranslation} from "react-i18next";
import domtoimage from "dom-to-image";
import {getFileSign} from "../../../utils/indexedDB";
import {signDocument} from "../../../services/signService";
import {createSignatureAppearance} from "../../../services/signatureAppearanceService";
import {useNavigate} from "react-router-dom"; // Asegúrate de ajustar la ruta según sea necesario
import SignatureAppearance from "../appearance/SignatureAppearance";
import {applySignaturePageMode} from "../../../utils/signatureRequest";
import {ComponentAlerts} from "../ComponentAlerts";
import {freezeComputedSizes, getDPR, isSafari, waitImagesReady} from "../../../utils/imageUtils";
import {isAllowedImageFileType, optimizeImageUploadFile} from "../../../utils/imageUploadOptimizer";
import {
  getPreviousSignaturePage,
  isAllPagesModeEnabled,
  SIGNATURE_PAGE_MODE_EVENT,
  updateAllPagesMode
} from '../../../utils/signaturePageMode';
import { getBrandLogoPng } from '../../../branding/brandings';

const logoSrc = getBrandLogoPng();

// --- WYSIWYG helpers ---
const PX_TO_PT = 72 / 96;
const MIN_SIGNATURE_TEMPLATE_HOST_WIDTH_PX = 520;
const MAX_SIGNATURE_TEMPLATE_HOST_WIDTH_PX = 1800;


async function waitFontsAndImages(root) {
  try {
    if (document.fonts?.ready) await document.fonts.ready;
  } catch {}
  const imgs = Array.from(root?.querySelectorAll('img') || []);
  await Promise.all(imgs.map(i => i.decode?.().catch(() => null)));
}

async function captureNodeToPNG(node) {
  if (!node) {
    throw new Error('La vista previa de la apariencia no esta disponible.');
  }
  await waitFontsAndImages(node);
  await waitImagesReady(node);
  const rect = node.getBoundingClientRect();
  if (rect.width < 1 || rect.height < 1) {
    throw new Error('La vista previa de la apariencia no tiene dimensiones validas.');
  }
  const dpr = getDPR();
    // Congelar tamaños reales (px) para que dom-to-image no reduzca el <img> (logo/firma)
        const restore = freezeComputedSizes(node);
    try {
        const imgs = Array.from(node?.querySelectorAll('img') || []);
        if (imgs.some(img => !(img.complete && img.naturalWidth > 0))) {
          await new Promise(r => setTimeout(r, 150));
        }
        const doCapture = () => domtoimage.toPng(node, {
              bgcolor: null,
              width:  Math.round(rect.width  * dpr),   // raster a DPR
              height: Math.round(rect.height * dpr),
              style: {
              transform: `scale(${dpr})`,
                  transformOrigin: 'top left',
                  width:  `${Math.round(rect.width)}px`,
                  height: `${Math.round(rect.height)}px`,
                }
        });
        if (isSafari()) {
          // Safari: warm-up render to avoid missing external images on first attempt
          try { await doCapture(); } catch {}
          await new Promise(r => setTimeout(r, 150));
        }
        let lastError = null;
        for (let attempt = 1; attempt <= 3; attempt += 1) {
          try {
            return await doCapture();
          } catch (error) {
            lastError = error;
            if (attempt < 3) {
              await waitImagesReady(node);
              await new Promise(r => setTimeout(r, 150 * attempt));
            }
          }
        }
        if (lastError instanceof Error) throw lastError;
        throw new Error('No se pudieron cargar todos los recursos de la apariencia.');
      } finally {
        restore(); // volver al estado normal del DOM
      }
}

function prepareHtmlTemplateForCapture(htmlTemplate) {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlTemplate, 'text/html');
    const root = doc.body.firstElementChild;
    if (root) {
      root.classList.add('signature-capture-clean');
    }
    return doc.body.innerHTML || htmlTemplate;
  } catch {
    return htmlTemplate;
  }
}

async function renderHtmlTemplateToPNG(htmlTemplate, hostWidthPx = 0) {
  if (typeof htmlTemplate !== 'string' || !htmlTemplate.trim()) return null;
  const preparedTemplate = prepareHtmlTemplateForCapture(htmlTemplate);

  const widthNum = Number(hostWidthPx);
  const baseWidth = Number.isFinite(widthNum) && widthNum > 0 ? Math.round(widthNum) : 420;
  const hostWidth = Math.max(MIN_SIGNATURE_TEMPLATE_HOST_WIDTH_PX, baseWidth);

  const mount = document.createElement('div');
  mount.style.position = 'fixed';
  mount.style.left = '-10000px';
  mount.style.top = '0';
  mount.style.pointerEvents = 'none';
  mount.style.zIndex = '-1';
  mount.style.background = '#ffffff';
  mount.style.width = `${hostWidth}px`;
  mount.innerHTML = `
    <div class="a-view">
      <div class="appearance-html-preview signature-capture-clean">
        <div class="appearance-html-preview__inner">${preparedTemplate}</div>
      </div>
    </div>
  `;
  document.body.appendChild(mount);

  try {
    await new Promise((resolve) => requestAnimationFrame(resolve));
    const target = mount.querySelector('.appearance-html-preview__inner') || mount.firstElementChild || mount;
    if (!target) return null;
    const preferredWidth = Math.ceil(
      Number(target.scrollWidth) ||
      Number(target.getBoundingClientRect().width) ||
      hostWidth
    );
    if (preferredWidth > hostWidth) {
      const expandedWidth = Math.min(MAX_SIGNATURE_TEMPLATE_HOST_WIDTH_PX, preferredWidth + 8);
      mount.style.width = `${expandedWidth}px`;
      await new Promise((resolve) => requestAnimationFrame(resolve));
    }
    return await captureNodeToPNG(target);
  } finally {
    mount.remove();
  }
}

async function getImageDimensionsFromDataUrl(dataUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({
      width: Number(img.naturalWidth || img.width || 0),
      height: Number(img.naturalHeight || img.height || 0),
    });
    img.onerror = reject;
    img.src = dataUrl;
  });
}

// Encaja el PNG a (appearance.width x appearance.height) SIN recortes (contain),
// conservando aspecto y añadiendo "bandas" (padding) simétricas para completar.
// padColor: '#fff' (blanco) o 'transparent' si prefieres transparencia.
// padAlpha: 0..1 (opacidad del relleno).
async function fitPngToDraggableBoxContain(dataUrl, padColor = 'transparent', padAlpha = 1) {
  let ap = {};
  try { ap = JSON.parse(sessionStorage.getItem('appearance') || '{}'); } catch {}
  const w = Number(ap?.width);
  const h = Number(ap?.height);
  if (!(w > 0 && h > 0)) return dataUrl; // sin caja aún, devolvemos original

  const img = new Image();
  img.src = dataUrl;
  await (img.decode?.() || Promise.resolve());

  const dpr = window.devicePixelRatio || 1;
  const targetW = Math.max(1, Math.round(w * dpr));
  const targetH = Math.max(1, Math.round(h * dpr));

  const canvas = document.createElement('canvas');
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext('2d');

  // Fondo (bandas) — blanco por defecto
  ctx.save();
  if (padColor === 'transparent') {
    // nada: queda transparente
  } else {
    ctx.globalAlpha = padAlpha;
    ctx.fillStyle = padColor; // '#fff' u otro color
    ctx.fillRect(0, 0, targetW, targetH);
  }
  ctx.restore();

  // Escalado uniforme (contain)
  const s = Math.min(targetW / (img.naturalWidth || 1), targetH / (img.naturalHeight || 1));
  const drawW = Math.round((img.naturalWidth || 1) * s);
  const drawH = Math.round((img.naturalHeight || 1) * s);
  const dx = Math.round((targetW - drawW) / 2);
  const dy = Math.round((targetH - drawH) / 2);

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, dx, dy, drawW, drawH);

  return canvas.toDataURL('image/png');
}



const ModalDefinirApariencia = forwardRef((_props, ref) => {
  const { t } = useTranslation();
  const [selectedOption, setSelectedOption] = useState('optionNinguna');
  const [signatureImage, setSignatureImage] = useState(null);
  const [isItalic, setIsItalic] = useState(false);
  const [alignment, setAlignment] = useState('center');
  const [flexAlignment, setFlexAlignment] = useState("center");
  const [showName, setShowName] = useState(true);
  const [showDate, setShowDate] = useState(false);
  const [showFreeText, setShowFreeText] = useState(false);
  const [showLabels, setShowLabels] = useState(false);
  const [freeText, setFreeText] = useState('');
  const signatureCanvasRef = useRef(null);
  const [isCanvasEmpty, setIsCanvasEmpty] = useState(false);
  const fileInputRef = useRef(null);
  const container = useRef(null);
  const [showLogo, setShowLogo] = useState(true); // State for the logo checkbox
  const fileName = sessionStorage.getItem('fileName');
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [pageInput, setPageInput] = useState('1');
  const [numPages, setNumPages] = useState(null);
  const [pageError, setPageError] = useState(false);
  const [allPages, setAllPages] = useState(isAllPagesModeEnabled);
  const [selectedFont, setSelectedFont] = useState("inherit");
  const [isBoldActive, setIsBoldActive] = useState(false);
  const [isItalicActive, setIsItalicActive] = useState(false);
  const [isUnderlineActive, setIsUnderlineActive] = useState(false);
  const editableRef = useRef();
  const [activeTab, setActiveTab] = useState('text');
  const [isDragOver, setIsDragOver] = useState(false);
  const [dateSelectedAt, setDateSelectedAt] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isImageProcessing, setIsImageProcessing] = useState(false);
  const [imageUploadError, setImageUploadError] = useState('');

  useEffect(() => {
    const syncPageMode = (event) => {
      const enabled = Boolean(event?.detail?.allPages);
      setAllPages(enabled);
      if (!enabled) {
        setPageInput(String(event?.detail?.previousPage || 1));
      }
      setPageError(false);
    };

    window.addEventListener(SIGNATURE_PAGE_MODE_EVENT, syncPageMode);
    return () => window.removeEventListener(SIGNATURE_PAGE_MODE_EVENT, syncPageMode);
  }, []);

  const unlockBodyAfterModalClose = () => {
    document.body.classList.remove('modal-open');
    document.body.style.removeProperty('overflow');
    document.body.style.removeProperty('padding-right');
    document.querySelectorAll('.modal-backdrop').forEach((backdrop) => backdrop.remove());
  };

  useImperativeHandle(ref, () => ({
    setDateSelectedAtExternal: (value) => {
      if (!value) {
        setDateSelectedAt(null);
        return;
      }
      const next = value instanceof Date ? value : new Date(value);
      if (!Number.isNaN(next.getTime())) {
        setDateSelectedAt(next);
      }
    },
    handleAccept,
  }), [handleAccept]);

    useEffect(() => {
        window.__generateSignatureAppearancePng = async ({ signDate }) => {
            // signDate: Date (momento firma)
            // 1) actualizar fecha en el estado / DOM
            if (signDate && showDate) {
                setDateSelectedAt(signDate);
                await new Promise(r => requestAnimationFrame(r));
            }

            // 2) generar png desde container.current, normalizado para no depender del dispositivo
            const htmlTemplate = container.current?.outerHTML || '';
            const rect = container.current?.getBoundingClientRect?.();
            const hostWidth = Math.round((Number(rect?.width) || 0) / 0.9);
            const rawDataUrl =
                await renderHtmlTemplateToPNG(htmlTemplate, hostWidth) ||
                await captureNodeToPNG(container.current);

            const appearance = JSON.parse(sessionStorage.getItem('appearance') || '{}');
            appearance.image = appearance.image || {};
            appearance.image.value = rawDataUrl;
            sessionStorage.setItem('appearance', JSON.stringify(appearance));
            sessionStorage.setItem('signatureAppearance', rawDataUrl);

            return rawDataUrl;
        };

        return () => { delete window.__generateSignatureAppearancePng; };
    }, [showDate, /* y deps necesarias */]);

    const pad2 = (n) => String(n).padStart(2, '0');

    const getSignatureAppearanceUserId = () => {
        try {
            const userinfo = JSON.parse(sessionStorage.getItem('userinfo') || '');
            const uuid = userinfo?.uuid;
            return typeof uuid === 'string' && uuid ? uuid : null;
        } catch {}
        return null;
    };

    const persistSignatureAppearance = async (htmlTemplate) => {
        const userId = getSignatureAppearanceUserId();
        if (!userId) {
            console.warn('No se pudo guardar la apariencia: userId no disponible.');
            return;
        }
        let appearance = {};
        try { appearance = JSON.parse(sessionStorage.getItem('appearance') || '{}'); } catch {}
        const configJson = {
            appearance,
            ui: {
                selectedOption,
                showLogo,
                showName,
                showDate,
                showFreeText,
                showLabels,
                alignment,
                selectedFont,
                namePartsEnabled,
                freeText,
                page: Number(pageInput) || 1,
            }
        };
        const payload = {
            userId,
            type: 'CUSTOM',
            isSelected: true,
            configJson,
            htmlTemplate
        };
        try {
            return await createSignatureAppearance(payload);
            //window.dispatchEvent(new CustomEvent('refresh-signature-appearances'));
        } catch (err) {
            console.error('No se pudo guardar la apariencia de firma.', err);
        }
    };
    const syncRefreshSelectionModal = () =>
        new Promise((resolve) => {
            window.dispatchEvent(new CustomEvent('refresh-signature-appearances-sync', { detail: { resolve } }));
        });

    const formatDateTimeWithTZ = (d) => {
        if (!d) return '';
        const day = pad2(d.getDate());
        const month = pad2(d.getMonth() + 1);
        const year = d.getFullYear();

        const hh = pad2(d.getHours());
        const mm = pad2(d.getMinutes());
        const ss = pad2(d.getSeconds());
        const offsetMin = -d.getTimezoneOffset();
        const sign = offsetMin >= 0 ? '+' : '-';
        const abs = Math.abs(offsetMin);
        const offH = pad2(Math.floor(abs / 60));
        const offM = pad2(abs % 60);

        // Formato solicitado: 17/12/2025 13:23:55 -03'00'
        return `${day}/${month}/${year} ${hh}:${mm}:${ss} ${sign}${offH}'${offM}'`;
    };

    const IMAGE_MAX_BYTES = 300 * 1024;
    const IMAGE_MAX_LONG_SIDE_PX = 800;

    const processImageFile = async (file) => {
        if (!file) return;

        if (!isAllowedImageFileType(file)) {
            setImageUploadError('signatureAppearance.invalid-image-format');
            if (fileInputRef.current) fileInputRef.current.value = '';
            return;
        }

        setIsImageProcessing(true);
        try {
            const result = await optimizeImageUploadFile(file, {
                maxBytes: IMAGE_MAX_BYTES,
                maxLongSidePx: IMAGE_MAX_LONG_SIDE_PX
            });

            setImageUploadError('');
            setSignatureImage(result.dataUrl);
        } finally {
            setIsImageProcessing(false);
        }

        // Permite re-seleccionar el mismo archivo (mismo nombre) sin que el input quede “igual”
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const preventDefaults = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };


    const handleLogoToggle = () => {
    setShowLogo(!showLogo);
  };
  const vistaPreviaClassName = `vistaPrevia firma-apariencia size-pre`;
  const minScale = 0.5; // La firma puede reducirse a la mitad
  const maxScale = 1.3;



    const toProperCase = (name) => {
      if (!name) return;
      return name
          .split(/[\s]+/)
          .map(
              (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
          )
          .join(' ');
    };


  function readUserinfo() {
    try {
      const b64 = sessionStorage.getItem('userinfo');
      if (!b64) return {};
      const json = JSON.parse(b64);
      return json || {};
    } catch { return {}; }
  }

  function extractNamePartsFromIDP(u) {
    const firstName = toProperCase(u.primer_nombre || u.first_name || u.given_name || '');
    const secondName = toProperCase(u.segundo_nombre || u.second_name || u.middle_name || '');
    const firstLastName = toProperCase(u.primer_apellido || u.last_name || u.family_name || '');
    const secondLastName = toProperCase(u.segundo_apellido || u.second_last_name || u.second_family_name || '');
    return { firstName, secondName, firstLastName, secondLastName };
  }


  function buildFullName(parts, enabled) {
    const seq = [
      enabled.firstName      ? parts.firstName      : '',
      enabled.secondName     ? parts.secondName     : '',
      enabled.firstLastName  ? parts.firstLastName  : '',
      enabled.secondLastName ? parts.secondLastName : '',
    ].filter(Boolean);
    return seq.join(' ').replace(/\s+/g, ' ').trim();
  }

  const idp = useMemo(() => readUserinfo(), []);
  const namePartsFromIDP = useMemo(() => extractNamePartsFromIDP(idp), [idp]);


  const visibleNameParts = useMemo(() => ({
    firstName:      !!namePartsFromIDP.firstName,
    secondName:     !!namePartsFromIDP.secondName,
    firstLastName:  !!namePartsFromIDP.firstLastName,
    secondLastName: !!namePartsFromIDP.secondLastName,
  }), [namePartsFromIDP]);


  function loadPersistedNameParts() {
    try {
      const ap = JSON.parse(sessionStorage.getItem('appearance') || '{}');
      const saved = ap?.text?.nameParts;
      if (saved && typeof saved === 'object') {
        const defaults = {
          firstName:      !!visibleNameParts.firstName,
          secondName:     !!visibleNameParts.secondName,
          firstLastName:  !!visibleNameParts.firstLastName,
          secondLastName: !!visibleNameParts.secondLastName,
        };
        return { ...defaults, ...saved };
      }
    } catch {}
    // por defecto: todo lo visible marcado
    return {
      firstName:      !!visibleNameParts.firstName,
      secondName:     !!visibleNameParts.secondName,
      firstLastName:  !!visibleNameParts.firstLastName,
      secondLastName: !!visibleNameParts.secondLastName,
    };
  }

  const [namePartsEnabled, setNamePartsEnabled] = useState(loadPersistedNameParts());

  const composedName = useMemo(
      () => buildFullName(namePartsFromIDP, namePartsEnabled),
      [namePartsFromIDP, namePartsEnabled]
  );

  useEffect(() => {
    try {
      const ap = JSON.parse(sessionStorage.getItem('appearance') || '{}');
      ap.text = ap.text || {};
      ap.text.nameParts = namePartsEnabled;             // persistir checks
      ap.text.nameValue = composedName;                 // persistir valor compuesto
      sessionStorage.setItem('appearance', JSON.stringify(ap));
    } catch {}
  }, [namePartsEnabled, composedName]);


  const toggleNamePart = (key) => {
    setNamePartsEnabled(prev => ({ ...prev, [key]: !prev[key] }));
  };





  const handleAuthorize = async () => {
    await fillAppearance();
  };
  const fillAppearance = async () => {
    const element = container.current;
    if (!element) return;

    try {
      const htmlTemplate = element.outerHTML;
      const rect = element.getBoundingClientRect();
      const hostWidth = Math.round((Number(rect?.width) || 0) / 0.9);
      const rawDataUrl =
          await renderHtmlTemplateToPNG(htmlTemplate, hostWidth) ||
          await captureNodeToPNG(element);

        // 1:1 — fijamos las dimensiones destino al tamaño de la vista previa (CSS px)
      try {
          const ap = JSON.parse(sessionStorage.getItem('appearance') || '{}');
          ap.width  = Math.max(1, Math.round(rect.width));
          ap.height = Math.max(1, Math.round(rect.height));
          sessionStorage.setItem('appearance', JSON.stringify(ap));
      } catch {}

      // Sin fit: usamos directamente la captura
      const fittedDataUrl = rawDataUrl;

      // Persistencias
      const appearance = JSON.parse(sessionStorage.getItem('appearance') || '{}');
      appearance.image = appearance.image || {};
      appearance.image.value = fittedDataUrl;
      sessionStorage.setItem('appearance', JSON.stringify(appearance));
      sessionStorage.setItem('signatureAppearance', await fittedDataUrl);

      // (Si aquí firmas de una vez en tu flujo original)
      callSignDocument();
    } catch (err) {
      console.error('oops, something went wrong!', err);
    }
  };


  const getConfig = () => {
    const configEncoded = sessionStorage.getItem('configs');
    if (configEncoded) {
      const configDecoded = atob(configEncoded); // Decodificar base64
      return JSON.parse(configDecoded); // Parsear a JSON
    }
    return null; // Retorna null si no existe la configuración
  };

  const callSignDocument = async () => {
    try {
      const pdfFile = await getFileSign(fileName);
      const appearanceData = JSON.parse(sessionStorage.getItem('appearance') || '{}');

      const lowerLeft  = appearanceData?.location?.frame?.lowerLeft;
      const upperRight = appearanceData?.location?.frame?.upperRight;
      if (!lowerLeft) throw new Error('lowerLeft ausente en appearance.location.frame');

      const x = Number(lowerLeft.x);
      const y = Number(lowerLeft.y);

      // Tamaño preferente: pdfWidth/pdfHeight (en puntos PDF)
      let width  = Number(appearanceData?.pdfWidth);
      let height = Number(appearanceData?.pdfHeight);

      if (!Number.isFinite(width) || !Number.isFinite(height)) {
        // Fallback seguro: diferencia UR-LL
        if (upperRight?.x != null && upperRight?.y != null) {
          width  = Math.abs(Number(upperRight.x) - x);
          height = Math.abs(Number(upperRight.y) - y);
        }
      }

      // Guard final
      width  = Number.isFinite(width)  ? width  : 0;
      height = Number.isFinite(height) ? height : 0;

      const pageNumber = appearanceData?.location?.page ?? 'last';

      // Imagen
        let base64Image =
            appearanceData?.image?.value ||
            sessionStorage.getItem('signatureAppearance') ||
            null;

        if (base64Image) {
            base64Image = String(base64Image)
                .replace(/^data:image\/[a-zA-Z]+;base64,/, '')
                .trim();

            if (base64Image === '') {
                base64Image = null;
            }
        }

        const appearance = {
            signature_details: {
                details: [{ type: 'text', value: ' ' }]
            }
        };

        if (base64Image) {
            appearance.background_image = {
                binary: base64Image
            };
        }

        const requestJson = applySignaturePageMode({
            signature_field: {
                location: {
                    page: { number: pageNumber },
                    rectangle: { x, y, width, height }
                },
                appearance
            }
        });


      const response = await signDocument(sessionStorage.getItem('access_token'), pdfFile, requestJson);
      if (!response.ok) {
        const respText = await response.text();
        throw new Error(`Error al firmar: ${response.status} - ${respText}`);
      }
      const respJson = await response.json();
      if (respJson?.tasks?.pending?.[0]?.url) {
        window.location.href = respJson.tasks.pending[0].url;
      }
    } catch (e) {
      setError('signatureAppearance.sign-error');
    }
  };


  const handleOptionChange = (event) => {
    const option = event.target.value;
    setSelectedOption(option);
    setImageUploadError('');

    if (option === 'optionNombre') {
      setIsItalic(true);
    } else {
      setIsItalic(false);
    }

    if (option === 'optionImg') {
     // fileInputRef.current.click();  // Abre automáticamente el explorador de archivos
    }

    if (option === 'optionNinguna') {
      setSignatureImage(null);
      setIsItalic(false);
    }
  };

    const handleImageUpload = async (event) => {
        const file = event.target.files?.[0];
        try {
            await processImageFile(file);
        } catch (e) {
            setImageUploadError('signatureAppearance.upload-image-error');
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };


    const handleAlignmentChange = (align) => {
    const alignmentMap = {
      left: "flex-start",
      right: "flex-end"
    };
    setFlexAlignment(alignmentMap[align] || align);
    setAlignment(align);
  };

  const getMousePos = (e) => {
    const canvas = signatureCanvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const handleMouseDown = (e) => {
    const pos = getMousePos(e);
    const context = signatureCanvasRef.current.getContext('2d');
    context.beginPath();
    context.moveTo(pos.x, pos.y);
  };

  const handleMouseMove = (e) => {
    if (e.buttons !== 1) return;
    const pos = getMousePos(e);
    const context = signatureCanvasRef.current.getContext('2d');
    context.lineTo(pos.x, pos.y);
    context.stroke();
    updateCanvasEmptyState();
  };

  const handleMouseUp = () => {
    const canvas = signatureCanvasRef.current;
    const context = canvas.getContext('2d');
    context.closePath();
    updateCanvasEmptyState(); // Verifica el estado del canvas después de terminar de dibujar
  };

// Obtener posición de un touch como getMousePos
  const getTouchPos = (e) => {
    const canvas = signatureCanvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches[0] || e.changedTouches[0];
    const scaleX = canvas.width  / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (touch.clientX - rect.left) * scaleX,
      y: (touch.clientY - rect.top)  * scaleY,
    };
  };

  const handleTouchStart = (e) => {
    e.preventDefault();
    const pos = getTouchPos(e);
    const ctx = signatureCanvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  };

  const handleTouchMove = (e) => {
    e.preventDefault();
    const pos = getTouchPos(e);
    const ctx = signatureCanvasRef.current.getContext('2d');
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    updateCanvasEmptyState();
  };

  const handleTouchEnd = (e) => {
    e.preventDefault();
    const ctx = signatureCanvasRef.current.getContext('2d');
    ctx.closePath();
    updateCanvasEmptyState();
  };
// MOBILE_FIX_END


  // Función para verificar si el canvas está vacío
  const updateCanvasEmptyState = () => {
    const canvas = signatureCanvasRef.current;
    const context = canvas.getContext('2d');
    const empty = context.getImageData(0, 0, canvas.width, canvas.height).data.every((value) => value === 0);
    setIsCanvasEmpty(empty);
  };

  // Función para borrar el canvas
  const clearSignatureCanvas = () => {
    const canvas = signatureCanvasRef.current;
    const context = canvas.getContext('2d');
    context.clearRect(0, 0, canvas.width, canvas.height);
    setIsCanvasEmpty(true); // Marcar el canvas como vacío después de borrarlo
  };

  // Función para guardar la firma como imagen y luego limpiar el canvas
  const saveSignatureCanvas = () => {
    const canvas = signatureCanvasRef.current;
    const imageURL = canvas.toDataURL(); // Convierte el contenido del canvas a una URL de imagen
    setSignatureImage(imageURL); // Guarda la URL de la imagen en el estado para mostrarla en la vista previa
    clearSignatureCanvas(); // Limpia el canvas después de guardar
  };


// HiDPI: el canvas interno se dimensiona a (CSS px × DPR) para que iPhone/Mac se vea nítido
  useEffect(() => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;

    const setup = () => {
      const dpr = Math.max(1, window.devicePixelRatio || 1);

      // Ancho CSS real del canvas (lo tienes con style: { width:'100%', height:'160px' })
      const rect = canvas.getBoundingClientRect();
      const cssWidth  = rect.width || 600;

      // Altura CSS: lee el estilo computado (coincide con tu 160px)
      const cssHeight = parseFloat(getComputedStyle(canvas).height) || rect.height || 160;

      // Tamaño interno (físico) = CSS px × DPR  → imagen nítida en Retina
      canvas.width  = Math.round(cssWidth  * dpr);
      canvas.height = Math.round(cssHeight * dpr);

      const ctx = canvas.getContext('2d');

      // ⚠️ No usamos setTransform(dpr, ...) porque TU código ya escala coordenadas
      // con getMousePos() usando (canvas.width / rect.width). Mantener así evita dobles escalas.

      // Estilos de trazo en "px físicos" → 2 CSS px visibles = 2 * DPR
      ctx.lineCap   = 'round';
      ctx.lineJoin  = 'round';
      ctx.strokeStyle = 'black';
      ctx.lineWidth   = 2 * dpr; // antes 2; ahora 2 CSS px reales en Retina
    };

    setup();

    // Reajusta si cambia el tamaño/orientación (iOS) o la ventana (Mac)
    const ro = new ResizeObserver(() => setup());
    ro.observe(canvas);

    const onResize = () => setup();
    window.addEventListener('orientationchange', onResize);
    window.addEventListener('resize', onResize);

    return () => {
      ro.disconnect();
      window.removeEventListener('orientationchange', onResize);
      window.removeEventListener('resize', onResize);
    };
  }, []);


  const getCurrentDate = () => {
    const today = new Date();
    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const year = String(today.getFullYear());
    return `${day}/${month}/${year}`;
  };


  async function handleAccept(options = {}) {
      const { returnToSelection = true, persist = true } = options || {};
      if (!container.current) return;
      if (isSaving || isImageProcessing) return;
      setIsSaving(true);
      const iframe = document.querySelector('.pdfViewerContainer iframe');
      iframe?.contentWindow?.postMessage(
          { type: 'SIGNATURE_APPEARANCE_LOADING', value: true },
          '*'
      );
    try {
      const X = Number(pageInput);
      const N = Number(numPages || 0);
      if (!Number.isInteger(X) || X <= 0 || (N > 0 && X > N)) {
        setPageError(true);
        return;
      }

      try {
        const iframe = document.querySelector('.pdfViewerContainer iframe');
        if (iframe?.contentWindow) {
          iframe.contentWindow.postMessage({ type: 'NAVIGATE_TO_PAGE', page: X }, '*');
        }
      } catch {}

      // Captura WYSIWYG
      const modalDefAp = document.getElementById('modalDefinirApariencia');
      const needsLayout = container.current.getClientRects().length === 0;
      let restoreModalStyles = null;
      if (needsLayout && modalDefAp) {
        const prev = {
          display: modalDefAp.style.display,
          position: modalDefAp.style.position,
          left: modalDefAp.style.left,
          top: modalDefAp.style.top,
          right: modalDefAp.style.right,
          transform: modalDefAp.style.transform,
          pointerEvents: modalDefAp.style.pointerEvents,
          visibility: modalDefAp.style.visibility,
          opacity: modalDefAp.style.opacity,
          hasShow: modalDefAp.classList.contains('show'),
        };
        modalDefAp.style.display = 'block';
        modalDefAp.style.position = 'fixed';
        modalDefAp.style.left = '-10000px';
        modalDefAp.style.top = '0';
        modalDefAp.style.right = 'auto';
        modalDefAp.style.transform = 'none';
        modalDefAp.style.pointerEvents = 'none';
        modalDefAp.style.visibility = 'visible';
        modalDefAp.style.opacity = '1';
        modalDefAp.classList.add('show');
        restoreModalStyles = () => {
          modalDefAp.style.display = prev.display;
          modalDefAp.style.position = prev.position;
          modalDefAp.style.left = prev.left;
          modalDefAp.style.top = prev.top;
          modalDefAp.style.right = prev.right;
          modalDefAp.style.transform = prev.transform;
          modalDefAp.style.pointerEvents = prev.pointerEvents;
          modalDefAp.style.visibility = prev.visibility;
          modalDefAp.style.opacity = prev.opacity;
          if (!prev.hasShow)
            modalDefAp.classList.remove('show');
        };
        await new Promise(r => requestAnimationFrame(r));
      }

      let rawDataUrl = '';
      let htmlTemplate = '';
      let rectAfterCapture = null;
      try {
        rawDataUrl = await captureNodeToPNG(container.current);
        htmlTemplate = container.current?.outerHTML || '';
        rectAfterCapture = container.current?.getBoundingClientRect?.();
      } finally {
        if (restoreModalStyles)
          restoreModalStyles();
      }

      let cssWidth = 0;
      let cssHeight = 0;
      try {
        const rect = rectAfterCapture || container.current.getBoundingClientRect();
        const ap = JSON.parse(sessionStorage.getItem('appearance') || '{}');
        const prevWidth = Number(ap.width);
        const prevHeight = Number(ap.height);
        const minSizePx = 20;
        const rectWidth = Math.max(0, Math.round(rect.width));
        const rectHeight = Math.max(0, Math.round(rect.height));
        const rectLooksValid = rectWidth >= minSizePx && rectHeight >= minSizePx;

        cssWidth = rectLooksValid ? rectWidth : (prevWidth >= minSizePx ? prevWidth : rectWidth);
        cssHeight = rectLooksValid ? rectHeight : (prevHeight >= minSizePx ? prevHeight : rectHeight);
      } catch {}

      const hostWidth = Math.round((Number(rectAfterCapture?.width) || 0) / 0.9);
      const normalizedDataUrl = await renderHtmlTemplateToPNG(htmlTemplate, hostWidth);
      const fittedDataUrl = normalizedDataUrl || rawDataUrl;

      let imageDims = null;
      try {
        imageDims = await getImageDimensionsFromDataUrl(fittedDataUrl);
      } catch {}

      try {
        const ap = JSON.parse(sessionStorage.getItem('appearance') || '{}');
        if (imageDims?.width > 0 && imageDims?.height > 0) {
          const aspect = imageDims.width / imageDims.height;
          if (cssHeight > 0) {
            cssWidth = Math.max(cssWidth, Math.round(cssHeight * aspect));
          }
          ap.baseW = imageDims.width;
          ap.baseH = imageDims.height;
          ap.imageAspect = aspect;
        }
        ap.width = Math.max(1, Math.round(cssWidth));
        ap.height = Math.max(1, Math.round(cssHeight || rectAfterCapture?.height || 0));
        sessionStorage.setItem('appearance', JSON.stringify(ap));
      } catch {}

      // Persistencias
      sessionStorage.setItem("signatureAppearance", fittedDataUrl);
      sessionStorage.setItem('signatureAppearanceHtml', htmlTemplate);

        const appearance = JSON.parse(sessionStorage.getItem('appearance') || '{}');
      appearance.image = appearance.image || {};
      appearance.image.value = fittedDataUrl;
      sessionStorage.setItem('appearance', JSON.stringify(appearance));

      // Refrescar en visor

      const rect = rectAfterCapture || container.current.getBoundingClientRect();
      const postWidth = cssWidth || Math.round(rect.width);
      const postHeight = cssHeight || Math.round(rect.height);
       iframe.contentWindow.postMessage(
             {
               type: 'UPDATE_SIGNATURE_APPEARANCE',
               imageData: fittedDataUrl,
               cssWidth: postWidth,
               cssHeight: postHeight,
               minScale, maxScale
         },
         '*'
      );

        let created = null;

        if (persist) {
            created = await persistSignatureAppearance(htmlTemplate);
            if (created?.id) {
                window.dispatchEvent(new CustomEvent('signature-appearance-created', { detail: { created } }));
            }
        }
        if (returnToSelection && isSelectionModalEnabled() && !created?.id) {
            await syncRefreshSelectionModal();
        }

        sessionStorage.setItem('customAppearanceDefined', 'true');
      const selectionEnabled = isSelectionModalEnabled();
      if (selectionEnabled && returnToSelection) {
        handleClose();
      } else {
        closeDefineModal();
      }
    } catch (error) {
      const message = error instanceof Error
          ? error.message
          : 'No se pudieron cargar todos los recursos de la apariencia.';
      alert("No se pudo generar la imagen: " + message);
      console.error(error);
    }
    finally {
        setIsSaving(false);
    }
  }

  function saveSelection() {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      return sel.getRangeAt(0);
    }
    return null;
  }

  function restoreSelection(range) {
    if (range) {
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    }
  }

  const handleFreeTextChange = (e) => {
    const range = saveSelection();
    setFreeText(e.target.innerHTML);
    requestAnimationFrame(() => restoreSelection(range));
  };

  useEffect(() => {
    const handleSelectionChange = () => {
      const sel = window.getSelection();
      if (!sel || !editableRef.current || !editableRef.current.contains(sel.anchorNode)) {
        setIsBoldActive(false);
        setIsItalicActive(false);
        setIsUnderlineActive(false);
        return;
      }
      setIsBoldActive(document.queryCommandState("bold"));
      setIsItalicActive(document.queryCommandState("italic"));
      setIsUnderlineActive(document.queryCommandState("underline"));
    };

    document.addEventListener("selectionchange", handleSelectionChange);
    return () => document.removeEventListener("selectionchange", handleSelectionChange);
  }, []);

  useEffect(() => {
    function handleOpenModal() {
      setOpen(true);
      setTimeout(() => {
        const modal = document.getElementById('modalDefinirApariencia');
        if (modal) {
          modal.classList.add('show');
          modal.style.display = 'block';
          document.body.classList.add('modal-open');
          if (!document.querySelector('.modal-backdrop')) {
            const backdrop = document.createElement('div');
            backdrop.className = 'modal-backdrop fade show';
            document.body.appendChild(backdrop);
          }
        }
      }, 10);

      const allPagesEnabled = isAllPagesModeEnabled();
      setAllPages(allPagesEnabled);
      setPageInput(allPagesEnabled ? String(getPreviousSignaturePage()) : '1');
      try {
        const iframe = document.querySelector('.pdfViewerContainer iframe');
        if (iframe?.contentWindow) {
          if (!allPagesEnabled) {
            iframe.contentWindow.postMessage({ type: 'PDF_CURRENT_PAGE' }, '*');
          }
          iframe.contentWindow.postMessage({ type: 'REQUEST_NUM_PAGES' }, '*');
        }
      } catch {}
    }
    window.addEventListener('open-define-appearance-modal', handleOpenModal);
    return () => window.removeEventListener('open-define-appearance-modal', handleOpenModal);
  }, []);
  
  useEffect(() => {
    const onMsg = (e) => {
      if (e?.data?.type === 'PDF_NUM_PAGES') {
        const n = Number(e.data?.payload?.numPages || 0);
        setNumPages(n > 0 ? n : null);
      }
      if (e?.data?.type === 'PDF_CURRENT_PAGE') {
        const p = Number(e.data?.payload?.page || 0);
        if (p > 0 && !isAllPagesModeEnabled()) setPageInput(String(p));
      }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, []);

  const sanitizeInt = (v) => String(v).replace(/[^\d]/g, '');
  const onPageChange = (e) => {
    const v = sanitizeInt(e.target.value);
    setPageInput(v);
    if (pageError) setPageError(false);
  };
  const onAllPagesChange = (e) => {
    const enabled = e.target.checked;
    const previousPage = updateAllPagesMode(enabled, pageInput);
    setAllPages(enabled);
    if (!enabled) setPageInput(String(previousPage));
    setPageError(false);
  };
  const onPageKeyDown = (e) => {
    const allowed = ['Backspace','Delete','ArrowLeft','ArrowRight','Home','End','Tab','Enter'];
    if (allowed.includes(e.key)) {
      if (e.key === 'Enter') handleAccept();
      return;
    }
    if (!/^\d$/.test(e.key)) e.preventDefault();
  };
  const isSelectionModalEnabled = () => {
    try {
      const cfg = JSON.parse(atob(sessionStorage.getItem('configs') || ''));
      const v = cfg?.SIGNATURE_APPEARANCE_MODAL;
      return (v === undefined ? true : !!v);
    } catch {
      return true;
    }
  };

  const closeDefineModal = () => {
    setOpen(false);
    const modal = document.getElementById('modalDefinirApariencia');
    if (modal) {
      modal.classList.remove('show');
      modal.style.display = 'none';
      unlockBodyAfterModalClose();
    }
  };

  function handleClose() {
    closeDefineModal();
    if (isSelectionModalEnabled()) {
      window.dispatchEvent(new CustomEvent('open-signature-appearance-modal'));
    }
  }

    const clearFreeText = (e) => {
        e.preventDefault();

        if (editableRef.current) {
            editableRef.current.innerHTML = '';
        }

        setFreeText('');

        // Reset toolbar state
        setIsBoldActive(false);
        setIsItalicActive(false);
        setIsUnderlineActive(false);
    };
    const previewDateText = useMemo(() => {
        if (!showDate) return null;
        const d = dateSelectedAt || new Date(); // fallback si algo queda desincronizado
        return  formatDateTimeWithTZ(d);
    }, [showDate, dateSelectedAt, showLabels]);

    return (
    <>
      <div className="component-modal">
        {/* Modal */}
        <div className="modal" id="modalDefinirApariencia">
          <div className="page-container">
            <div className="content-modal content-modal-xl">
              {/* Head modal */}
              <div className="header-modal">
                <h2 className="title-modal">CONFIGURAR APARIENCIA</h2>
                <span className="close-modal" onClick={handleClose}>&times;</span>
              </div>

              {/* Body modal */}
              <div className="body-modal">
                {pageError && (
                  <div style={{marginBottom: '12px'}}>
                    <ComponentAlerts alertType="error" alertMessageKey={error || 'error.pageNotExists'}/>
                  </div>
                )}
                <div className="definirApariencia-content">
                    <div className="apariencia-column configSection">
                        <div className="tab-wrapper">
                            <div className="nav-tabs" role="tablist">
                                <a  href="#configuraciondetexto"
                                    className={`tab-item-link ${activeTab === 'text' ? 'active' : ''}`}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setActiveTab('text');
                                    }}
                                    role="tab"
                                    aria-selected={activeTab === 'text'}
                                    aria-controls="configuraciondetexto"
                                > {t('signatureAppearance.text-configuration')} </a>
                                <a  href="#configuraciongrafica"
                                    className={`tab-item-link ${activeTab === 'graphic' ? 'active' : ''}`}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setActiveTab('graphic');
                                    }}
                                    role="tab"
                                    aria-selected={activeTab === 'graphic'}
                                    aria-controls="configuraciongrafica"
                                > {t('signatureAppearance.graphic-configuration')} </a>
                            </div>
                            <div className="content-tabs">
                                {/* TAB: CONFIGURACIÓN DE TEXTO */}
                                <div className={`tab-content ${activeTab === 'text' ? 'active' : ''}`} id="configuraciondetexto">
                                    <div className="section section-texto">
                                        <p className="title">{t('signatureAppearance.text-alignment')}</p>
                                        <div className="text-align">
                                            <button
                                                className={`btn-small btn-align ${alignment === 'left' ? 'active' : ''}`}
                                                type="button"
                                                onClick={() => handleAlignmentChange('left')}
                                            >
                                                <span className="material-symbols-outlined">format_align_left</span>
                                            </button>
                                            <button
                                                className={`btn-small btn-align ${alignment === 'justify' ? 'active' : ''}`}
                                                type="button"
                                                onClick={() => handleAlignmentChange('justify')}
                                            >
                                                <span className="material-symbols-outlined">format_align_justify</span>
                                            </button>
                                            <button
                                                className={`btn-small btn-align ${alignment === 'center' ? 'active' : ''}`}
                                                type="button"
                                                onClick={() => handleAlignmentChange('center')}
                                            >
                                                <span className="material-symbols-outlined">format_align_center</span>
                                            </button>
                                            <button
                                                className={`btn-small btn-align ${alignment === 'right' ? 'active' : ''}`}
                                                type="button"
                                                onClick={() => handleAlignmentChange('right')}
                                            >
                                                <span className="material-symbols-outlined">format_align_right</span>
                                            </button>
                                        </div>
                                        <p className="title">{t('signatureAppearance.text-elements')}</p>
                                        <form>
                                            {/* CONFIGURACIÓN DE TEXTO */}
                                            <div className="form-group">
                                                {visibleNameParts.firstName && (
                                                    <div>
                                                        <input
                                                            className="form-checkbox"
                                                            id="firstName"
                                                            type="checkbox"
                                                            checked={!!namePartsEnabled.firstName}
                                                            onChange={() => toggleNamePart('firstName')}
                                                        />
                                                        <label className="form-label" htmlFor="firstName">{t('signatureAppearance.first-name')}</label>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="form-group">
                                                {visibleNameParts.secondName && (
                                                    <div>
                                                        <input
                                                            className="form-checkbox"
                                                            id="secondName"
                                                            type="checkbox"
                                                            checked={!!namePartsEnabled.secondName}
                                                            onChange={() => toggleNamePart('secondName')}
                                                        />
                                                        <label className="form-label" htmlFor="secondName">{t('signatureAppearance.second-name')}</label>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="form-group">
                                                {visibleNameParts.firstLastName && (
                                                    <div>
                                                        <input
                                                            className="form-checkbox"
                                                            id="firstLastName"
                                                            type="checkbox"
                                                            checked={!!namePartsEnabled.firstLastName}
                                                            onChange={() => toggleNamePart('firstLastName')}
                                                        />
                                                        <label className="form-label" htmlFor="firstLastName">{t('signatureAppearance.first-last-name')}</label>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="form-group">
                                                {visibleNameParts.secondLastName && (
                                                    <div>
                                                        <input
                                                            className="form-checkbox"
                                                            id="secondLastName"
                                                            type="checkbox"
                                                            checked={!!namePartsEnabled.secondLastName}
                                                            onChange={() => toggleNamePart('secondLastName')}
                                                        />
                                                        <label className="form-label" htmlFor="secondLastName">{t('signatureAppearance.second-last-name')}</label>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="form-group">
                                                <input
                                                    className="form-checkbox"
                                                    id="checkbox13"
                                                    type="checkbox"
                                                    checked={showDate}
                                                    onChange={() => {
                                                        setShowDate((prev) => {
                                                            const next = !prev;
                                                            if (next) {
                                                                const now = new Date();
                                                                setDateSelectedAt(now);
                                                                try {
                                                                    const ap = JSON.parse(sessionStorage.getItem('appearance') || '{}');
                                                                    ap.text = ap.text || {};
                                                                    ap.text.dateSelectedAtIso = now.toISOString();
                                                                    sessionStorage.setItem('appearance', JSON.stringify(ap));
                                                                } catch {}
                                                            } else {
                                                                setDateSelectedAt(null);
                                                                try {
                                                                    const ap = JSON.parse(sessionStorage.getItem('appearance') || '{}');
                                                                    if (ap?.text) {
                                                                        delete ap.text.dateSelectedAtIso;
                                                                        sessionStorage.setItem('appearance', JSON.stringify(ap));
                                                                    }
                                                                } catch {}
                                                            }
                                                            return next;
                                                        });
                                                    }}
                                                />
                                                <label className="form-label" htmlFor="checkbox13">{t('signatureAppearance.date')}</label>
                                            </div>

                                            <div className="form-group">
                                                <input
                                                    className="form-checkbox"
                                                    id="inputtextoLibre"
                                                    type="checkbox"
                                                    checked={showFreeText}
                                                    onChange={() => setShowFreeText(!showFreeText)}
                                                />
                                                <label className="form-label" htmlFor="inputtextoLibre">{t('signatureAppearance.free-text')}</label>
                                            </div>

                                            {showFreeText && (
                                                <div>
                                                <div className="form-group text-config">
                                                    <div
                                                        className="toolbar"
                                                        style={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: "6px",
                                                            marginBottom: "6px",
                                                            width: "100%",
                                                        }}
                                                    >
                                                        {/* Fuente */}
                                                        <select
                                                            value={selectedFont}
                                                            onChange={(e) => {
                                                                const newFont = e.target.value;
                                                                setSelectedFont(newFont);
                                                                if (editableRef.current) {
                                                                    editableRef.current.style.fontFamily =
                                                                        newFont === "inherit" ? "inherit" : newFont;
                                                                }
                                                            }}
                                                            className="form-select"
                                                            style={{
                                                                flexGrow: 1,
                                                                padding: "2px 4px",
                                                                fontSize: "13px",
                                                                height: "35px",
                                                            }}
                                                        >
                                                            <option value="inherit">{t('signatureAppearance.normal-font')}</option>
                                                            <option value="sans-serif">Sans Serif</option>
                                                            <option value="fantasy">Fantasy</option>
                                                            <option value="Courier New">Courier New</option>
                                                            <option value="Alex Brush">Alex Brush</option>
                                                        </select>

                                                        <div style={{display: "flex", gap: "6px"}}>
                                                            {/* Negrita */}
                                                            <button
                                                                type="button"
                                                                className={`btn-small btn-format ${isBoldActive ? "active" : ""}`}
                                                                onClick={() => document.execCommand("bold")}
                                                            >
                                                                <span
                                                                    className="material-symbols-outlined">format_bold</span>
                                                            </button>

                                                            {/* Cursiva */}
                                                            <button
                                                                type="button"
                                                                className={`btn-small btn-format ${isItalicActive ? "active" : ""}`}
                                                                onClick={() => document.execCommand("italic")}
                                                            >
                                                                <span
                                                                    className="material-symbols-outlined">format_italic</span>
                                                            </button>

                                                            {/* Subrayado */}
                                                            <button
                                                                type="button"
                                                                className={`btn-small btn-format ${isUnderlineActive ? "active" : ""}`}
                                                                onClick={() => document.execCommand("underline")}
                                                            >
                                                                <span
                                                                    className="material-symbols-outlined">format_underlined</span>
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                                    <div
                                                        ref={editableRef}
                                                        className="form-group textoLibre"
                                                        contentEditable
                                                        suppressContentEditableWarning
                                                        onInput={handleFreeTextChange}
                                                    />
                                                    <a className="borrar-txt" href="#" onClick={clearFreeText}>{t('signatureAppearance.clear')} </a>
                                                </div>
                                            )}

                                            {/* Etiquetas */}
                                            <div className="form-group">
                                                <input
                                                    className="form-checkbox"
                                                    id="checkboxEtiquetas"
                                                    type="checkbox"
                                                    checked={showLabels}
                                                    onChange={() => setShowLabels(!showLabels)}
                                                />
                                                <label className="form-label" htmlFor="checkboxEtiquetas">
                                                    {t('signatureAppearance.labels')}
                                                </label>
                                            </div>
                                        </form>
                                    </div>
                                </div>

                                {/* TAB: CONFIGURACIÓN GRÁFICA */}
                                <div className={`tab-content ${activeTab === 'graphic' ? 'active' : ''}`} id="configuraciongrafica">
                                    <div className="section section-grafica">
                                        <form>
                                            <div className="card-inputs">
                                                    <label className="option-card">
                                                        <input
                                                            className="form-checkbox"
                                                            id="radiofirmaManuscrita"
                                                            type="radio"
                                                            name="inlineRadioOptions"
                                                            value="optionfirmaManuscrita"
                                                            onChange={handleOptionChange}
                                                        />
                                                        <span className="material-symbols-outlined"
                                                              aria-hidden="true"> draw </span>
                                                        <div className="text">
                                                            <span className="title">{t('signatureAppearance.handwritten-signature')}</span>
                                                            <span className="subtitle">{t('signatureAppearance.handwritten-signature-subtitle')}</span>
                                                        </div>
                                                    </label>

                                                <label className="option-card">
                                                    <input
                                                        className="form-checkbox"
                                                        id="radioImg"
                                                        type="radio"
                                                        name="inlineRadioOptions"
                                                        value="optionImg"
                                                        onChange={handleOptionChange}
                                                    />
                                                    <span className="material-symbols-outlined"
                                                          aria-hidden="true"> add_photo_alternate </span>
                                                    <div className="text">
                                                        <span className="title">{t('signatureAppearance.image')}</span>
                                                        <span className="subtitle">{t('signatureAppearance.image-subtitle')}</span>
                                                    </div>

                                                </label>

                                                <label className="option-card">
                                                    <input
                                                        className="form-checkbox"
                                                        id="radioNombre"
                                                        type="radio"
                                                        name="inlineRadioOptions"
                                                        value="optionNombre"
                                                        onChange={handleOptionChange}
                                                    />
                                                    <span className="material-symbols-outlined"
                                                          aria-hidden="true"> text_fields </span>
                                                    <div className="text">
                                                        <span className="title">{t('signatureAppearance.cursive-name')}</span>
                                                        <span className="subtitle">{t('signatureAppearance.cursive-name-subtitle')}</span>
                                                    </div>
                                                </label>

                                                <label className="option-card">
                                                    <input
                                                        className="form-checkbox"
                                                        id="radioNinguna"
                                                        type="radio"
                                                        name="inlineRadioOptions"
                                                        value="optionNinguna"
                                                        checked={selectedOption === 'optionNinguna'}
                                                        onChange={handleOptionChange}
                                                    />
                                                    <span className="material-symbols-outlined"
                                                          aria-hidden="true"> cancel </span>
                                                    <div className="text">
                                                        <span className="title">{t('signatureAppearance.none')}</span>
                                                        <span className="subtitle">{t('signatureAppearance.none-subtitle')}</span>
                                                    </div>
                                                </label>
                                            </div>
                                            <div className="form-group">
                                                <input
                                                    className="form-checkbox"
                                                    id="checkbox6"
                                                        type="checkbox"
                                                        onChange={handleLogoToggle}
                                                        defaultChecked
                                                    />
                                                    <label className="form-label" htmlFor="checkbox6">
                                                        {t('signatureAppearance.logo')}
                                                    </label>
                                                </div>
                                            {selectedOption === 'optionfirmaManuscrita' && (
                                                <div className="opt-content">
                                                    <p className="item-heading" style={{color: 'var(--semantic-accent-default)', fontWeight: 'bold'}} >
                                                        {t('signatureAppearance.draw-signature')}
                                                    </p>
                                                    <div className="dibujar-firma">
                                                        <canvas
                                                            id="signatureCanvas"
                                                            ref={signatureCanvasRef}
                                                            onMouseDown={handleMouseDown}
                                                            onMouseMove={handleMouseMove}
                                                            onMouseUp={handleMouseUp}
                                                            onTouchStart={handleTouchStart}
                                                            onTouchMove={handleTouchMove}
                                                            onTouchEnd={handleTouchEnd}
                                                            style={{width: '100%', maxWidth: '100%', height: '160px'}}
                                                        ></canvas>
                                                    </div>
                                                        <div className="actions">
                                                            <button
                                                                type="button"
                                                                className="btn-xsmall btn-alternative"
                                                                onClick={clearSignatureCanvas}
                                                            >
                                                                {t('signatureAppearance.clear')}
                                                            </button>
                                                            <button
                                                                type={"button"}
                                                                className={`btn-xsmall btn-primary-outline ${isCanvasEmpty ? 'disable-link' : ''}`}
                                                                onClick={saveSignatureCanvas}
                                                            >
                                                                {t('signatureAppearance.apply')}
                                                            </button>
                                                        </div>

                                                </div>
                                            )}
                                            {/* cargar imagen*/}
                                            {selectedOption === 'optionImg' && (
                                                <div className="component-carga-documentos">
                                                    <div className={`carga-documento-content ${isDragOver ? 'drag-over' : ''} ${imageUploadError ? 'carga-documento-content-error' : ''}`}
                                                         onDragEnter={(e) => { preventDefaults(e); setIsDragOver(true); }}
                                                         onDragOver={(e) => { preventDefaults(e); setIsDragOver(true); }}
                                                         onDragLeave={(e) => { preventDefaults(e); setIsDragOver(false); }}
                                                         onDrop={async (e) => {
                                                             preventDefaults(e);
                                                             setIsDragOver(false);

                                                             const file = e.dataTransfer?.files?.[0];
                                                             try {
                                                                 await processImageFile(file);
                                                             } catch (err) {
                                                                 setImageUploadError('signatureAppearance.upload-image-error');
                                                                 if (fileInputRef.current) fileInputRef.current.value = '';
                                                             }
                                                         }}>
                                                        <span className="material-symbols-outlined"> upload </span>
                                                        <p className="text"> {t('signatureAppearance.drag-image')} </p>
                                                        <p className="text-small"> {t('signatureAppearance.or-prefer')} </p>
                                                        <input
                                                            className="file-upload-input"
                                                            type="file"
                                                            ref={fileInputRef}
                                                            accept="image/png,image/jpeg"
                                                            onChange={handleImageUpload}
                                                        />
                                                        <a className="local-upload btn-medium btn-primary-outline"
                                                           href="#" data-toggle="tooltip" data-placement="top" title=""
                                                           onClick={(e) => {
                                                               e.preventDefault();
                                                               fileInputRef.current?.click();
                                                           }}
                                                           data-original-title="Desktop"> {t('signatureAppearance.select-image-file')} </a>

                                                        <p className="text-alert">{t('signatureAppearance.image-alert')}</p>
                                                        {imageUploadError && (
                                                            <p className="image-upload-error" role="alert">
                                                                <span className="material-symbols-outlined" aria-hidden="true">image_not_supported</span>
                                                                {t(imageUploadError)}
                                                            </p>
                                                        )}
                                                    </div>

                                                </div>
                                            )}

                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                        {/* tab-wrapper */}
                    </div>
                    <div className="apariencia-column previewSection">
                        <div className="wrapper">
                            <div className="item" tabIndex="0">
                                <p className="item-heading" tabIndex="0">
                                    {t('signatureAppearance.preview')}
                                </p>
                                <div id="item" className="item-content" style={{ display: 'block' }}>
                        <div className="apariencia-preview">
                          <div className="a-view">
                              <div className="signaturePreviewBox">
                               <div ref={container} className="a-content">
                               <SignatureAppearance
                                     /* Logo siempre que el checkbox esté activo */
                               logoSrc={showLogo ? logoSrc : null}
                               showLogo={!!showLogo}

                               /* Firma/Imagen del usuario: opción imagen o manuscrita */
                               signatureSrc={
                              (selectedOption === 'optionImg' || selectedOption === 'optionfirmaManuscrita')
                               ? signatureImage
                                   : null
                                  }

                               /* Textos controlados por tu UI */
                               name={showName ? (showLabels ? `${t('signatureAppearance.name-label')}: ${composedName}` : composedName) : null}
                               date={previewDateText}
                               freeText={showFreeText ? freeText : null}
                               selectedFont={selectedFont}
                               showName={showName}
                               showDate={showDate}
                               showFreeText={showFreeText}

                               /* Alineación de CONFIGURACIÓN DE TEXTO: afecta logo, firma e incluso textos */
                               alignment={alignment}
                               nameItalic={selectedOption === 'optionNombre' && isItalic}
                               showLabels={showLabels}
                               logoPosition={getConfig()?.DEFAULT_PAGE_SIGNATURE_LOGO_POSITION ?? 'BEFORE_TEXT'}
                               />
                               </div>

                            </div>
                          </div>
                        </div>
                      </div>
                    </div>


    </div>
                    </div>
                </div>
               </div>

  <div className="pages-config">
      <div className="pages-select">
          <div className="form-group-inline">
              <label className="form-label" htmlFor="sig-page-input-define">
                  {t('signatureAppearance.previewOnPage')}
              </label>
                  <input
                      id="sig-page-input-define"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={pageInput}
                      onChange={onPageChange}
                      onKeyDown={onPageKeyDown}
                      disabled={allPages}
                      aria-invalid={pageError ? 'true' : 'false'}
                      className={`num-input ${pageError ? 'input-error' : ''}`}
                  />
                  <span className="sep">/</span>
                  <span className="badge-muted">{numPages ?? '—'}</span>
          </div>
      </div>
      <div className="form-group all-pages-toggle">
          <input
              className="form-checkbox"
              id="all-signature-pages-define"
              type="checkbox"
              checked={allPages}
              onChange={onAllPagesChange}
          />
          <label className="form-label" htmlFor="all-signature-pages-define">
              {t('signatureAppearance.allPages')}
          </label>
      </div>
  </div>

      {/* Footer modal */}
      <div className="footer-modal">
  <div className="actions">
    <button type="button" className="btn-small btn-secondary modal-trigger" data-dismiss="modal" onClick={handleClose} disabled={isSaving || isImageProcessing}>
      {t('signatureAppearance.back')}
    </button>
    <button type="button" className="btn-small btn-primary" onClick={handleAccept} disabled={isSaving || isImageProcessing}>
      {t('signatureAppearance.confirm')}
    </button>
  </div>
</div>
</div>
</div>
</div>
</div>
    </>
  );

});

export default ModalDefinirApariencia;
